import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { FolderItem } from "src/app/api/services/folder";

export type ListItem<T extends { folderId?: string }> = {
  item: T;
  isFolder: false;
  selected: boolean;
} | {
  item: FolderItem;
  isFolder: true;
  selected: boolean;
}

@Injectable()
export class ScrollingListStateService<T extends { folderId?: string }> {
  private readonly list$ = new BehaviorSubject<ListItem<T>[]>([]);
  private readonly error$ = new BehaviorSubject<{apiError: string, errorMessage: string} | null>(null);
  public readonly maxCount = 40;
  private name: string = '';
  private cursor: string | null = null;
  private endOfList = false;
  private allSelected = false;
  private readonly loadingItems$ = new BehaviorSubject<boolean>(false);

  list() {
    return this.list$.asObservable();
  }

  add(items: ListItem<T>[], cursor: string | null) {
    this.list$.next(this.sortFoldersFirst([...this.list$.value, ...items]));
    this.cursor = cursor;
    this.endOfList = items.length < this.maxCount;
  }

  isEndOfList() {
    return this.endOfList;
  }

  clear() {
    this.list$.next([]);
    this.cursor = null;
    this.endOfList = false;
    this.allSelected = false;
  }

  loadingItems() {
    return this.loadingItems$.asObservable();
  }

  isLoadingItems() {
    return this.loadingItems$.value;
  }

  setLoadingItems(loading: boolean) {
    this.loadingItems$.next(loading);
  }

  currentCursor() {
    return this.cursor;
  }

  currentList() {
    return this.list$.value;
  }

  selectAll() {
    this.list$.value.forEach(item => {
      item.selected = true;
    });
    this.allSelected = true;
  }

  deselectAll() {
    this.list$.value.forEach(item => {
      item.selected = false;
    });
    this.allSelected = false;
  }

  select(item: ListItem<T>, selected?: true) {
    item.selected = selected !== undefined ? selected : !item.selected;
    this.allSelected = this.list$.value
      .every(item => item.selected);
  }

  deselect(item: ListItem<T>) {
    item.selected = false;
    this.allSelected = this.list$.value.every(item => item.selected);
  }

  isAllSelected() {
    return this.allSelected;
  }

  isAllUnselected() {
    return this.list$.value.every(item => !item.selected);
  }

  isAnySelected() {
    return this.list$.value.some(item => item.selected);
  }

  getSelected = () => {
    return this.list$.value.filter(item => item.selected);
  };

  setError(error: {apiError: string, errorMessage: string}) {
    this.error$.next(error);
  }

  error() {
    return this.error$.asObservable();
  }

  currentError() {
    return this.error$.value;
  }

  setName(name: string) {
    this.name = name;
  }

  getName() {
    return this.name;
  }

  remove(item: ListItem<T>) {
    this.list$.next(this.list$.value.filter(i => i !== item));
  }

  clearMessages() {
    this.loadingItems$.next(false);
    this.error$.next(null);
  }

  private sortFoldersFirst(list: ListItem<T>[]) {
    return list.sort((a, b) => {
      if (a.isFolder && !b.isFolder) return -1;
      if (!a.isFolder && b.isFolder) return 1;
      return 0;
    });
  }
}